import { useCallback, useEffect, useState }         from 'react'
import {
  Badge, Banner, BlockStack, Card, DataTable,
  InlineGrid, InlineStack, Page, Select, Spinner, Text,
} from '@shopify/polaris'
import { useParams }                                 from 'react-router-dom'
import { getHistoryStats, getHistoryActivity }       from '../../../sdk/history.api'
import type { HistoryStatsResponse, HistoryActivityResponse, QueueActivityTab } from '../../../sdk/history.api'

type WorkflowParam = 'cf' | 'sc' | 'st' | 'cs'

const TAB_MAP: Record<WorkflowParam, QueueActivityTab> = {
  cf: 'CF_Activity',
  sc: 'SC_Activity',
  st: 'ST_Activity',
  cs: 'CS_Activity',
}

const LABEL_MAP: Record<WorkflowParam, string> = {
  cf: 'Confirmation',
  sc: 'Shipping Confirmation',
  st: 'Sales Team',
  cs: 'Customer Service',
}

function todayIso() { return new Date().toISOString().slice(0, 10) }
function yesterday() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

const DATE_OPTIONS = [
  { label: 'Today',     value: todayIso()   },
  { label: 'Yesterday', value: yesterday()  },
]

type Props = {
  workflowOverride?: WorkflowParam
  backUrl?: string
}

export function WorkflowHistoryPage({ workflowOverride, backUrl }: Props = {}) {
  const { workflow = 'cf' } = useParams<{ workflow: WorkflowParam }>()
  const wf    = (workflowOverride ?? (workflow in TAB_MAP ? workflow : 'cf')) as WorkflowParam
  const tab   = TAB_MAP[wf]
  const label = LABEL_MAP[wf]

  const [date,         setDate]         = useState(todayIso())
  const [stats,        setStats]        = useState<HistoryStatsResponse | null>(null)
  const [activity,     setActivity]     = useState<HistoryActivityResponse | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)
  const [loadingLog,   setLoadingLog]   = useState(false)
  const [error,        setError]        = useState<string | null>(null)

  const load = useCallback((d: string) => {
    setError(null)
    setLoadingStats(true)
    setLoadingLog(true)

    getHistoryStats(tab, d)
      .then(setStats)
      .catch(() => setError('Failed to load stats'))
      .finally(() => setLoadingStats(false))

    getHistoryActivity(tab, d, undefined, 200)
      .then(setActivity)
      .catch(() => {})
      .finally(() => setLoadingLog(false))
  }, [tab])

  useEffect(() => { load(date) }, [date, load])

  const handleDateChange = (val: string) => {
    setDate(val)
  }

  // ── Leaderboard table ──────────────────────────────────────────────────────
  const leaderboardRows = (stats?.leaderboard ?? []).map(e => [
    `#${e.rank}`,
    e.agent,
    e.completedSessions,
    `${e.ahtMin} min`,
    e.topOutcome ?? '—',
  ])

  // ── Activity log table ─────────────────────────────────────────────────────
  const ACTIVITY_CORE_COLS = ['timestamp', 'agent', 'action', 'order_name', 'duration_min']
  const activityRows = (activity?.rows ?? []).slice(0, 100).map(row =>
    ACTIVITY_CORE_COLS.map(col => row[col] ?? ''),
  )

  const anomalies = stats?.anomalies ?? []

  return (
    <Page
      title={`${label} — History`}
      subtitle={date}
      backAction={{ content: backUrl ? label : 'History', url: backUrl ?? '/boarding/history' }}
    >
      <BlockStack gap="600">

        {/* Date picker */}
        <InlineStack gap="300" blockAlign="center">
          <div style={{ width: 200 }}>
            <Select
              label="Date"
              options={DATE_OPTIONS}
              value={date}
              onChange={handleDateChange}
            />
          </div>
          {(loadingStats || loadingLog) && <Spinner size="small" />}
        </InlineStack>

        {error && <Banner tone="critical"><p>{error}</p></Banner>}

        {/* Summary stats */}
        <InlineGrid columns={{ xs: 2, sm: 4 }} gap="300">
          {[
            { label: 'Active agents',   value: stats?.agents.length ?? '—'                                                    },
            { label: 'Sessions done',   value: stats?.agents.reduce((a, s) => a + s.completedSessions, 0) ?? '—'             },
            { label: 'Avg AHT',         value: stats && stats.agents.length
                ? `${Math.round(stats.agents.reduce((a, s) => a + s.ahtMin, 0) / stats.agents.length * 10) / 10} min` : '—' },
            { label: 'Anomaly flags',   value: anomalies.length                                                               },
          ].map(m => (
            <Card key={m.label}>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">{m.label}</Text>
                <Text as="p" variant="headingXl">{String(m.value)}</Text>
              </BlockStack>
            </Card>
          ))}
        </InlineGrid>

        {/* Anomaly flags */}
        {anomalies.length > 0 && (
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">Anomaly Flags</Text>
              <BlockStack gap="200">
                {anomalies.map((a, i) => (
                  <InlineStack key={i} gap="200" blockAlign="center">
                    <Badge tone={a.type === 'too_fast' ? 'critical' : a.type === 'no_checkout' ? 'warning' : 'attention'}>
                      {a.type.replace(/_/g, ' ')}
                    </Badge>
                    <Text as="span" variant="bodySm">{a.agent}</Text>
                    <Text as="span" variant="bodySm" tone="subdued">{a.detail}</Text>
                  </InlineStack>
                ))}
              </BlockStack>
            </BlockStack>
          </Card>
        )}

        {/* Leaderboard */}
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Leaderboard</Text>
            {loadingStats
              ? <Spinner size="small" />
              : leaderboardRows.length === 0
                ? <Text as="p" variant="bodySm" tone="subdued">No data for this date.</Text>
                : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                    headings={['Rank', 'Agent', 'Sessions', 'AHT', 'Top Outcome']}
                    rows={leaderboardRows}
                  />
                )
            }
          </BlockStack>
        </Card>

        {/* Per-agent breakdown */}
        {(stats?.agents ?? []).length > 0 && (
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">Agent Breakdown</Text>
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'text', 'text', 'text', 'numeric']}
                headings={['Agent', 'Sessions', 'Done', 'AHT', 'Check-in', 'Check-out', 'Break (min)']}
                rows={(stats?.agents ?? []).map(a => [
                  a.agent,
                  a.sessions,
                  a.completedSessions,
                  `${a.ahtMin} min`,
                  a.checkinTime ? a.checkinTime.slice(11, 16) : '—',
                  a.checkoutTime ? a.checkoutTime.slice(11, 16) : '—',
                  a.totalBreakMin,
                ])}
              />
            </BlockStack>
          </Card>
        )}

        {/* Activity log */}
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">Activity Log</Text>
              {activity && (
                <Text as="p" variant="bodySm" tone="subdued">
                  Showing {activityRows.length} of {activity.total} rows
                </Text>
              )}
            </InlineStack>
            {loadingLog
              ? <Spinner size="small" />
              : activityRows.length === 0
                ? <Text as="p" variant="bodySm" tone="subdued">No activity recorded for this date.</Text>
                : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'numeric']}
                    headings={['Time', 'Agent', 'Action', 'Order', 'Duration (min)']}
                    rows={activityRows}
                  />
                )
            }
          </BlockStack>
        </Card>

      </BlockStack>
    </Page>
  )
}
